/**
 * KLineProvider - K线数据上下文
 * 
 * 提供统一的K线历史数据获取：
 * - K线历史数据
 * - 实时数据合并
 * - 技术指标计算
 */

import { createContext, useContext, useCallback, ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api/client';
import { useTradingSession } from './TradingSessionProvider';
import { useRealtimeQuote } from './RealtimeProvider';
import type { KLineHistoryData, KLineBar, IndicatorData } from './types';

interface KLineContextValue {
  invalidateHistory: (symbol: string) => void;
}

const KLineContext = createContext<KLineContextValue | null>(null);

export function KLineProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();

  const invalidateHistory = useCallback((symbol: string) => {
    queryClient.invalidateQueries({ queryKey: ['kline-history', symbol] });
  }, [queryClient]);

  return (
    <KLineContext.Provider
      value={{
        invalidateHistory,
      }}
    >
      {children}
    </KLineContext.Provider>
  );
}

export function useKLineContext(): KLineContextValue {
  const context = useContext(KLineContext);
  if (!context) {
    throw new Error('useKLineContext must be used within KLineProvider');
  }
  return context;
}

/**
 * Hook: 获取K线历史数据（自动合并实时数据）
 */
export function useKLineHistory(
  symbol: string,
  options?: {
    period?: string;
    startDate?: string;
    endDate?: string;
    limit?: number;
    includeRealtime?: boolean;
    enabled?: boolean;
  }
) {
  const { session } = useTradingSession();
  const includeRealtime = options?.includeRealtime !== false;
  
  // 获取实时行情用于合并
  const { data: realtimeQuote } = useRealtimeQuote(symbol, {
    enabled: !!symbol && includeRealtime && session.isTradingDay,
  });

  const historyQuery = useQuery({
    queryKey: ['kline-history', symbol, options?.period, options?.limit],
    queryFn: async () => {
      const { data } = await apiClient.getQuoteHistory(symbol, {
        period: options?.period,
        start_date: options?.startDate,
        end_date: options?.endDate,
      });
      return transformKLineData(data, symbol);
    },
    enabled: !!symbol && (options?.enabled !== false),
    refetchInterval: session.isTradingHours ? 60000 : 300000,
    staleTime: session.isTradingHours ? 30000 : 120000,
    refetchOnWindowFocus: false,
  });

  // 合并实时数据到最后一根K线
  const mergedData = historyQuery.data
    ? mergeRealtimeBar(historyQuery.data, realtimeQuote ? {
        ...realtimeQuote,
        tradeDate: realtimeQuote.tradeDate,
      } : undefined)
    : undefined;

  return {
    ...historyQuery,
    data: mergedData,
  };
}

/**
 * Hook: 获取K线数据和技术指标
 */
export function useKLineWithIndicators(
  symbol: string,
  indicators: string[] = ['MA', 'RSI', 'MACD'],
  options?: {
    period?: string;
    limit?: number;
    enabled?: boolean;
  }
) {
  const { session } = useTradingSession();

  return useQuery({
    queryKey: ['kline-with-indicators', symbol, indicators.join(','), options?.limit],
    queryFn: async () => {
      const { data } = await apiClient.getIndicators(symbol, {
        indicators,
        period: options?.period,
      });
      return data as IndicatorData[];
    },
    enabled: !!symbol && (options?.enabled !== false),
    staleTime: session.isTradingHours ? 30000 : 300000,
  });
}

/**
 * 转换API数据为统一格式
 */
function transformKLineData(data: unknown, symbol: string): KLineHistoryData {
  const d = data as Record<string, unknown>;
  const bars = (d.data as unknown[]) || [];
  
  return {
    symbol,
    name: d.name as string | undefined,
    period: String(d.period || 'daily'),
    data: bars.map((bar: unknown) => {
      const b = bar as Record<string, unknown>;
      return {
        timestamp: String(b.timestamp || b.date || ''),
        open: Number(b.open || 0),
        high: Number(b.high || 0),
        low: Number(b.low || 0),
        close: Number(b.close || 0),
        volume: Number(b.volume || 0),
        amount: b.amount ? Number(b.amount) : undefined,
      };
    }),
    hasRealtimeBar: Boolean(d.has_realtime_bar || d.hasRealtimeBar),
    totalBars: bars.length,
    lastUpdate: d.last_update as string | undefined,
  };
}

/**
 * 合并实时行情到K线数据
 * 
 * 策略：
 * - 使用 quote 的实际交易日期，而不是当前日期
 * - 确保非交易日也能正确合并最新交易日数据
 */
function mergeRealtimeBar(
  history: KLineHistoryData,
  quote?: { 
    close: number; 
    open: number; 
    high: number; 
    low: number; 
    volume: number; 
    amount?: number;
    tradeDate?: string;
  }
): KLineHistoryData {
  if (!quote || !history.data.length || quote.close <= 0) {
    return history;
  }

  // 使用 quote 的实际交易日期
  let quoteDate = quote.tradeDate || new Date().toISOString().split('T')[0];
  // 取日期部分
  if (quoteDate.includes('T')) {
    quoteDate = quoteDate.split('T')[0];
  }
  
  const lastBar = history.data[history.data.length - 1];
  let lastBarDate = lastBar.timestamp;
  if (lastBarDate.includes('T')) {
    lastBarDate = lastBarDate.split('T')[0];
  }

  // 创建实时K线
  const realtimeBar: KLineBar = {
    timestamp: quoteDate,
    open: quote.open || quote.close,
    high: quote.high || quote.close,
    low: quote.low || quote.close,
    close: quote.close,
    volume: quote.volume || 0,
    amount: quote.amount,
  };

  // 检查最后一根K线的日期
  if (lastBarDate === quoteDate) {
    // 更新当日K线（增量更新）
    const updatedBars = [...history.data];
    updatedBars[updatedBars.length - 1] = {
      timestamp: quoteDate,
      open: lastBar.open, // 保留原始开盘价
      high: Math.max(lastBar.high, realtimeBar.high),
      low: realtimeBar.low > 0 ? Math.min(lastBar.low, realtimeBar.low) : lastBar.low,
      close: realtimeBar.close,
      volume: realtimeBar.volume,
      amount: realtimeBar.amount,
    };
    return { ...history, data: updatedBars, hasRealtimeBar: true };
  } else if (quoteDate > lastBarDate) {
    // 添加新的K线（新交易日）
    return {
      ...history,
      data: [...history.data, realtimeBar],
      hasRealtimeBar: true,
    };
  }
  
  // 如果 quoteDate < lastBarDate，不做任何操作（数据已是最新）
  return history;
}
